import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { BulkPointEditBody, Edge, Site } from '../api/types.js';
import { useApi, useAuth } from '../auth.js';
import { Age, ErrorBox, LiveValueText, QualityBadge, Spinner, StatusFlags } from '../components.js';
import { useDebounced, useLiveValues, useNow, useResource } from '../hooks.js';
import { formatCount, formatPath, siemensPath } from '../lib/format.js';
import { newest } from '../lib/live.js';
import { pageInfo } from '../lib/paging.js';
import { isAnalogType } from '../lib/selection.js';
import { unitSymbol } from '../lib/units.js';
import { BulkPointEditor, Limits, TagChips } from '../tags.js';

export const PAGE_SIZE = 50;

interface SiteEdges {
  site: Site;
  edges: Edge[];
}

export function PointsPage() {
  const api = useApi();
  const now = useNow(1000);
  const [params, setParams] = useSearchParams();
  const siteId = params.get('site_id') ?? '';
  const edgeId = params.get('edge_id') ?? '';
  const q = params.get('q') ?? '';
  const tagFilters = params.getAll('tag');
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  // The search box follows the URL (back/forward) and writes to it debounced.
  const [search, setSearch] = useState(q);
  const [shownQ, setShownQ] = useState(q);
  if (q !== shownQ) {
    setShownQ(q);
    setSearch(q);
  }
  const debounced = useDebounced(search, 300);
  const lastDebounced = useRef(debounced);

  function update(next: Record<string, string>, replace = false) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace });
  }

  /** Tag filters are AND-ed: adding keeps the others. */
  function setTags(tags: string[]) {
    const p = new URLSearchParams(params);
    p.delete('tag');
    for (const t of [...new Set(tags)]) p.append('tag', t);
    p.delete('page');
    setParams(p);
  }

  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  // Bulk tags and limits: operators and up (ADR 0010); the API checks each point's site.
  const canEdit = useAuth().user?.permissions.operate ?? false;
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<Error | null>(null);

  useEffect(() => {
    if (debounced === lastDebounced.current) return;
    lastDebounced.current = debounced;
    const text = debounced.trim();
    if (text === q) return;
    const p = new URLSearchParams(params);
    if (text) p.set('q', text);
    else p.delete('q');
    p.delete('page');
    setParams(p, { replace: true });
  }, [debounced, q, params, setParams]);

  const filters = useResource<SiteEdges[]>('points-filters', async () => {
    const sites = await api.sites();
    return Promise.all(sites.map(async (site) => ({ site, edges: await api.edges(site.id) })));
  });

  const offset = (page - 1) * PAGE_SIZE;
  const key = JSON.stringify([siteId, edgeId, q, tagFilters, offset]);
  const points = useResource(key, (signal) =>
    api.points(
      {
        site_id: siteId || undefined,
        edge_id: edgeId || undefined,
        q: q || undefined,
        tag: tagFilters.length ? tagFilters : undefined,
        limit: PAGE_SIZE,
        offset,
      },
      signal,
    ),
  );
  const tags = useResource(`tags:${siteId}:${edgeId}`, (signal) =>
    api.tags({ site_id: siteId || undefined, edge_id: edgeId || undefined }, signal),
  );

  const items = useMemo(() => points.data?.items ?? [], [points.data]);
  const ids = useMemo(() => items.map((p) => p.id), [items]);
  const seeds = useMemo(() => items.map((p) => p.live), [items]);
  const { live, status } = useLiveValues(ids, seeds);

  const checkedOnPage = items.filter((p) => checked.has(p.id));
  const allChecked = items.length > 0 && checkedOnPage.length === items.length;
  // Checked points known to be non-analog (those on this page); the API checks the rest.
  const checkedPoints = useMemo(() => {
    const byId = new Map(items.map((p) => [p.id, p]));
    return [...checked].map((id) => byId.get(id));
  }, [checked, items]);
  const analogChecked =
    checked.size - checkedPoints.filter((p) => p && !isAnalogType(p.object_type)).length;

  async function applyBulk(body: Omit<BulkPointEditBody, 'point_ids'>) {
    setEditing(true);
    setEditError(null);
    try {
      await api.updatePoints({ ...body, point_ids: [...checked] });
      points.reload();
      tags.reload();
    } catch (err) {
      setEditError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setEditing(false);
    }
  }

  const edgeOptions = (filters.data ?? []).filter((g) => !siteId || g.site.id === siteId);
  const info = points.data ? pageInfo(points.data.total, offset, PAGE_SIZE) : null;

  return (
    <section>
      <div className="page-head">
        <h1>Points</h1>
        <span className={`live-status live-${status}`} title="Live value connection">
          <span className="dot" aria-hidden="true" />
          {status === 'open'
            ? 'Live'
            : status === 'idle'
              ? 'Idle'
              : status === 'connecting'
                ? 'Connecting…'
                : 'Reconnecting…'}
        </span>
      </div>

      <div className="toolbar">
        <input
          type="search"
          className="search"
          placeholder="Search name, key, metric, description, path…"
          aria-label="Search points"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <select
          aria-label="Site"
          value={siteId}
          onChange={(e) => {
            update({ site_id: e.target.value, edge_id: '', page: '' });
          }}
        >
          <option value="">All sites</option>
          {filters.data?.map((g) => (
            <option key={g.site.id} value={g.site.id}>
              {g.site.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Edge"
          value={edgeId}
          onChange={(e) => {
            update({ edge_id: e.target.value, page: '' });
          }}
        >
          <option value="">All edges</option>
          {edgeOptions.map((g) => (
            <optgroup key={g.site.id} label={g.site.name}>
              {g.edges.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.edge_node_id}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <select
          aria-label="Add a tag filter"
          value=""
          onChange={(e) => {
            if (e.target.value) setTags([...tagFilters, e.target.value]);
          }}
        >
          <option value="">Filter by tag…</option>
          {tags.data?.map((t) => (
            <option key={t.tag} value={t.tag} disabled={tagFilters.includes(t.tag)}>
              {t.tag} ({formatCount(t.count)})
            </option>
          ))}
        </select>
      </div>
      {tagFilters.length > 0 && (
        <div className="toolbar chips" role="group" aria-label="Tag filters">
          {tagFilters.map((t) => (
            <button
              key={t}
              type="button"
              className="chip active"
              title="Remove this tag filter"
              onClick={() => {
                setTags(tagFilters.filter((x) => x !== t));
              }}
            >
              {t} ×
            </button>
          ))}
        </div>
      )}
      {checked.size > 0 && canEdit && (
        <div className="toolbar bulk">
          <span className="muted small">{formatCount(checked.size)} points checked:</span>
          <BulkPointEditor
            count={checked.size}
            analogCount={analogChecked}
            busy={editing}
            onApply={(body) => {
              void applyBulk(body);
            }}
          />
          <button
            type="button"
            className="btn btn-ghost btn-small"
            onClick={() => {
              setChecked(new Set());
            }}
          >
            Uncheck all
          </button>
        </div>
      )}
      {editError && <ErrorBox error={editError} />}

      {points.error && <ErrorBox error={points.error} onRetry={points.reload} />}
      {!points.data && points.loading && <Spinner />}
      {points.data && items.length === 0 && <p className="empty">No points match.</p>}
      {items.length > 0 && (
        <div className={`table-wrap ${points.loading ? 'is-loading' : ''}`}>
          <table className="table table-points">
            <thead>
              <tr>
                <th className="check">
                  <input
                    type="checkbox"
                    aria-label="Check all points on this page"
                    checked={allChecked}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setChecked((c) => {
                        const next = new Set(c);
                        for (const p of items) {
                          if (on) next.add(p.id);
                          else next.delete(p.id);
                        }
                        return next;
                      });
                    }}
                  />
                </th>
                <th>Name</th>
                <th>Point key</th>
                <th>Metric</th>
                <th>Device</th>
                <th>Unit</th>
                <th>Tags · limits</th>
                <th className="num">Value</th>
                <th>Quality</th>
                <th title="Status flags: A in alarm, F fault, O overridden, S out of service">
                  Flags
                </th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const v = newest(p.live, live.values.get(p.id));
                return (
                  <tr key={p.id}>
                    <td className="check">
                      <input
                        type="checkbox"
                        aria-label={`Check ${p.name || p.metric_name}`}
                        checked={checked.has(p.id)}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setChecked((c) => {
                            const next = new Set(c);
                            if (on) next.add(p.id);
                            else next.delete(p.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td>
                      <Link to={`/points/${p.id}`} className="strong" title={p.description}>
                        {p.name || p.metric_name}
                      </Link>
                      {p.description && <div className="small">{p.description}</div>}
                      {siemensPath(p) && (
                        <div className="muted small">
                          {formatPath(siemensPath(p), p.description)}
                        </div>
                      )}
                    </td>
                    <td className="mono small">{p.point_key}</td>
                    <td className="mono small">{p.metric_name}</td>
                    <td className="mono small">
                      {p.device.sparkplug_device_id}
                      <div className="muted">{p.edge.edge_node_id}</div>
                    </td>
                    <td>{unitSymbol(p.unit)}</td>
                    <td className="small tags-cell">
                      <TagChips
                        tags={p.tags}
                        onPick={(t) => {
                          setTags([...tagFilters, t]);
                        }}
                      />
                      <Limits min={p.min_value} max={p.max_value} units={p.unit} />
                    </td>
                    <td className="num">
                      <LiveValueText
                        dataType={p.data_type}
                        unit=""
                        live={v}
                        flashKey={live.changes.get(p.id)}
                      />
                    </td>
                    <td>
                      <QualityBadge q={v?.q} />
                    </td>
                    <td>
                      <StatusFlags sf={v?.sf} />
                    </td>
                    <td className="small">
                      <Age ts={v?.ts} now={now} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {info && info.total > 0 && (
        <div className="pager">
          <span className="muted">
            {formatCount(info.first)}–{formatCount(info.last)} of {formatCount(info.total)}
          </span>
          <button
            type="button"
            className="btn btn-small"
            disabled={!info.hasPrev}
            onClick={() => {
              update({ page: page > 2 ? String(page - 1) : '' });
            }}
          >
            Previous
          </button>
          <span className="muted small">
            Page {info.page} of {info.pages}
          </span>
          <button
            type="button"
            className="btn btn-small"
            disabled={!info.hasNext}
            onClick={() => {
              update({ page: String(page + 1) });
            }}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}
